export const GA_MEASUREMENT_ID = 'G-9T7GKQ8BK7';

export type AnalyticsParams = Record<string, string | number | boolean | undefined | null>;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    [key: `ga-disable-${string}`]: boolean | undefined;
  }
}

function hasAnalyticsConsent() {
  if (typeof window === 'undefined') return false;
  try {
    const stored = localStorage.getItem('cookie_consent_preferences');
    if (!stored) return false;
    const prefs = JSON.parse(stored) as { analytics?: boolean };
    return prefs.analytics === true;
  } catch {
    return false;
  }
}

function sanitizeParams(params: AnalyticsParams = {}) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
  );
}

export function trackEvent(name: string, params: AnalyticsParams = {}) {
  if (typeof window === 'undefined' || !window.gtag || !hasAnalyticsConsent()) return;
  window.gtag('event', name, sanitizeParams(params));
}

export function trackPageView(path: string, title?: string) {
  trackEvent('page_view', {
    page_path: path,
    page_title: title ?? document.title,
    page_location: typeof window !== 'undefined' ? window.location.href : undefined,
  });
}

export function trackScrollDepth(percent: 50 | 90, path: string) {
  trackEvent('scroll_depth', {
    percent_scrolled: percent,
    page_path: path,
  });
}

export function trackTimeOnPage(seconds: number, path: string) {
  trackEvent('time_on_page', {
    seconds_on_page: Math.round(seconds),
    page_path: path,
  });
}

export function trackCTA(label: string, location: string, href?: string) {
  trackEvent('cta_click', {
    cta_label: label,
    cta_location: location,
    destination: href,
  });
}

export function trackSignupClick(location: string, href?: string) {
  trackEvent('signup_click', {
    cta_location: location,
    destination: href,
  });
}

export function trackSignup(method: string, location: string) {
  trackEvent('signup', {
    method,
    signup_location: location,
  });
}

export function trackToolUsage(toolSlug: string, action: string, title?: string) {
  trackEvent('tool_usage', {
    tool_slug: toolSlug,
    tool_action: action,
    tool_title: title,
  });
}

export function trackToolCompletion(toolSlug: string, title?: string, completionMethod?: string) {
  trackEvent('tool_completion', {
    tool_slug: toolSlug,
    tool_title: title,
    completion_method: completionMethod,
  });
}
